/**
 * Sanitization utility using DOMPurify
 * 
 * Used to sanitize user-generated content and API responses before displaying
 * them in the UI. Prevents XSS attacks and malicious JavaScript injection.
 * 
 * Usage:
 *   import { sanitizeHtml, sanitizeText } from './sanitize';
 *   
 *   // For HTML content (rare)
 *   const clean = sanitizeHtml(userContent);
 *   
 *   // For plain text (common in messages/alerts)
 *   const clean = sanitizeText(userMessage);
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content - removes all scripts and dangerous elements.
 * Use sparingly; prefer sanitizeText for user messages.
 */
export const sanitizeHtml = (html) => {
    if (typeof html !== 'string') {
        return '';
    }
    // ALLOWED_TAGS and ALLOWED_ATTR can be customized based on needs
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p'],
        ALLOWED_ATTR: [],
    });
};

/**
 * Sanitize plain text - encodes HTML entities to prevent XSS.
 * Use for user messages, alert text, and display content.
 */
export const sanitizeText = (text) => {
    if (typeof text !== 'string') {
        return '';
    }
    // DOMPurify with ALLOWED_TAGS: [] will encode HTML entities
    return DOMPurify.sanitize(text, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
    });
};

/**
 * Sanitize object values recursively.
 * Useful for sanitizing API response objects before display.
 */
export const sanitizeObject = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }

    if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObject(item));
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'string') {
            sanitized[key] = sanitizeText(value);
        } else if (typeof value === 'object' && value !== null) {
            sanitized[key] = sanitizeObject(value);
        } else {
            sanitized[key] = value;
        }
    }
    return sanitized;
};

const sanitizeModule = {
    sanitizeHtml,
    sanitizeText,
    sanitizeObject,
};

export default sanitizeModule;
